import { TEST_QUEUE } from '@/shared/routes'

/**
 * Which Focus queue the URL asks for, checked before anything is fetched.
 *
 * The value comes from the address bar, so it can be anything: a colleague who
 * no longer takes leads, a typo, or a link opened by someone without the right
 * to view other queues. The API answers an unknown seller with an empty queue
 * and a non-admin with 403, and neither is a clean fallback, so the app only
 * passes on a value that names a current seller to a viewer allowed to see it.
 */
export type QueueResolution =
  /** Not decidable yet: the viewer or the colleague list is still loading. */
  | { kind: 'pending' }
  /** The viewer's own queue. `drop` asks the caller to clear the parameter. */
  | { kind: 'own'; drop: boolean }
  | { kind: 'seller'; email: string }
  /** The admin test queue of QA test leads (YCRM-93). */
  | { kind: 'test' }

export interface QueueContext {
  /** The viewer's email, undefined while loading. */
  me: string | undefined
  /** Whether the viewer may view other queues (manage_users), undefined while loading. */
  canViewOthers: boolean | undefined
  /** Colleagues, undefined while loading. */
  colleagues: ReadonlyArray<{ email: string; takesLeads: boolean }> | undefined
}

export function resolveQueueParam(raw: string | null, ctx: QueueContext): QueueResolution {
  const wanted = (raw ?? '').trim().toLowerCase()
  if (!wanted) return { kind: 'own', drop: raw !== null }
  if (ctx.me === undefined || ctx.canViewOthers === undefined) return { kind: 'pending' }
  if (!ctx.canViewOthers || wanted === ctx.me.toLowerCase()) return { kind: 'own', drop: true }
  if (wanted === TEST_QUEUE) return { kind: 'test' }
  if (ctx.colleagues === undefined) return { kind: 'pending' }
  const seller = ctx.colleagues.find(c => c.takesLeads && c.email.toLowerCase() === wanted)
  return seller ? { kind: 'seller', email: seller.email } : { kind: 'own', drop: true }
}
